<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { recruitmentApi } from '@/services/api'
import BModal from '@/components/common/BModal.vue'
import ScreeningReviewCard from '@/components/recruitment/screening/review/ScreeningReviewCard.vue'
import ModalComment from '@/components/recruitment/screening/ModalComment.vue'
import EditCandidateInformations from '@/components/recruitment/EditCandidateInformations.vue'
import SvgSpinnersPulseRings3 from '~icons/svg-spinners/pulse-rings-3'

const { t } = useI18n()

const props = defineProps<{
  processId: string
}>()

const isOpen = defineModel<boolean>('isOpen', { default: false })

const application = ref<any>(null)
const isLoading = ref(false)
const commentApplication = ref<any>(null)
const isEditOpen = ref(false)

// The screening review's sheet, whatever the stage of the process
const loadApplication = async () => {
  isLoading.value = true
  try {
    const response = await recruitmentApi.screeningList({ processId: props.processId, callLater: 'any', page: 0, limit: 1 }).promise
    application.value = response.docs?.[0] || null
  }
  catch {
    application.value = null
  }
  finally {
    isLoading.value = false
  }
}

// Reloaded at each opening: comments and status may have moved since
watch(isOpen, (open) => {
  if (open) loadApplication()
}, { immediate: true })
</script>

<template>
  <BModal
    v-model="isOpen"
    class="w-[95vw] max-w-7xl h-[90dvh]"
    data-cy="chat-candidate-sheet"
  >
    <template #header>
      {{ t('modules.chat.embed.candidateSheet') }}
    </template>
    <div
      v-if="isLoading && !application"
      class="flex flex-1 items-center justify-center"
    >
      <SvgSpinnersPulseRings3 class="text-5xl text-primary-700" />
    </div>
    <ScreeningReviewCard
      v-else-if="application"
      v-model="application"
      hide-actions
      @open-comments="commentApplication = application"
      @open-edit-candidate-informations="isEditOpen = true"
    />
    <p
      v-else
      class="flex flex-1 items-center justify-center text-center text-sm subtitle"
      data-cy="chat-candidate-sheet-not-found"
    >
      {{ t('modules.chat.embed.candidateNotFound') }}
    </p>
  </BModal>
  <ModalComment v-model:application="commentApplication" />
  <EditCandidateInformations
    v-model:modal-open="isEditOpen"
    :process-id="processId"
    @saved="loadApplication"
  />
</template>
